import React, { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../services/_apiBase';
import { fetchPriceList, savePrice as savePriceApi, deletePriceRow, fetchSpecification, fetchSmsOverview } from '../services/billingApi';
import { fetchTenants } from '../services/superuserApi';
import PageHeader from './shared/PageHeader';
import TenantModules from './billing/TenantModules';
import { colors, ui } from '../styles/shared';

import { BillingCategory, TenantInfo, PriceRow, PriceList, SpecCategory, Specification, SmsOverview } from '../types/billing';

const FALLBACK_LABELS: Record<BillingCategory, string> = {
    base: 'Grundavgift (en samordnare ingår)',
    sensor: 'Sensorer',
    heatpump: 'Värmepumpar',
    coordinator: 'Extra samordnare',
    inspections: 'Egenkontroll, modul',
    sms: 'Sms utöver det som ingår (kr per sms)',
    sms_included: 'Sms som ingår per samordnare (antal per månad)',
};
const CATEGORY_ORDER: BillingCategory[] = ['base', 'sensor', 'heatpump', 'coordinator', 'inspections', 'sms', 'sms_included'];

const kr = (n: number) => n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';

/** Föregående månad som YYYY-MM — det man oftast fakturerar. */
function previousMonth(): string {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Tabellstilar från ui-kitet, med högerställda varianter för siffror
const th = ui.th;
const td = ui.td;
const thRight: React.CSSProperties = { ...th, textAlign: 'right' };
const tdRight: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const card = ui.card;
const priceInput: React.CSSProperties = { ...ui.input, width: 80, padding: '6px 8px', fontSize: 13, textAlign: 'right' };

const Billing: React.FC = () => {
    const [tenants, setTenants] = useState<TenantInfo[]>([]);
    const [tenantId, setTenantId] = useState('');
    const [month, setMonth] = useState(previousMonth());
    const [spec, setSpec] = useState<Specification | null>(null);
    const [specLoading, setSpecLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showDetails, setShowDetails] = useState(true);

    const [priceList, setPriceList] = useState<PriceList | null>(null);
    const [showPrices, setShowPrices] = useState(false);
    const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});   // key: tenant|category|tier
    const [overrideTenant, setOverrideTenant] = useState('');
    const [priceMsg, setPriceMsg] = useState<string | null>(null);
    const [smsOverview, setSmsOverview] = useState<SmsOverview | null>(null);

    const labels = priceList?.labels ?? FALLBACK_LABELS;
    const isQuantity = (c: BillingCategory) => (priceList?.quantities ?? ['sms_included']).includes(c);
    const tiers = priceList?.tiers ?? [{ min: 1, label: '1–5' }, { min: 6, label: '6–10' }, { min: 11, label: '11+' }];
    const isTiered = (c: BillingCategory) => (priceList?.tiered ?? ['sensor', 'heatpump', 'coordinator']).includes(c);

    useEffect(() => {
        fetchTenants()
            .then(all => {
                const active = all.filter(t => t.active);
                setTenants(active);
                if (active.length > 0) setTenantId(prev => prev || active[0].id);
            })
            .catch(err => setError(getErrorMessage(err, 'Kunde inte hämta kundlistan')));
    }, []);

    const loadPrices = useCallback(() => {
        fetchPriceList()
            .then(list => { setPriceList(list); setPriceDrafts({}); })
            .catch(() => {});
    }, []);

    useEffect(() => { loadPrices(); }, [loadPrices]);

    const loadSpec = useCallback(() => {
        if (!tenantId || !/^\d{4}-\d{2}$/.test(month)) return;
        setSpecLoading(true);
        setError(null);
        fetchSpecification(tenantId, month)
            .then(setSpec)
            .catch(err => {
                setSpec(null);
                setError(getErrorMessage(err, 'Kunde inte hämta underlaget'));
            })
            .finally(() => setSpecLoading(false));
    }, [tenantId, month]);

    useEffect(() => { loadSpec(); }, [loadSpec]);

    // Sms per kund för samma månad — en överblick innan man fakturerar.
    useEffect(() => {
        if (!/^\d{4}-\d{2}$/.test(month)) return;
        fetchSmsOverview(month).then(setSmsOverview).catch(() => setSmsOverview(null));
    }, [month]);

    // ── Prislista ───────────────────────────────────────────────────────────
    const draftKey = (tenant: string, category: BillingCategory, tier: number) => `${tenant}|${category}|${tier}`;
    const rowFor = (tenant: string, category: BillingCategory, tier: number): PriceRow | undefined =>
        priceList?.prices.find(p => p.tenant_id === tenant && p.category === category && p.tier_min === tier);

    const savePrice = (tenant: string, category: BillingCategory, tier: number) => {
        const key = draftKey(tenant, category, tier);
        const draft = priceDrafts[key];
        if (draft === undefined) return;
        const value = Number(draft.replace(',', '.'));
        if (!Number.isFinite(value) || value < 0) { setPriceMsg('Ogiltigt pris'); return; }
        savePriceApi({ category, tenantId: tenant, tierMin: tier, monthlyPrice: value })
            .then(() => { setPriceMsg('Sparat'); loadPrices(); loadSpec(); })
            .catch(err => setPriceMsg(getErrorMessage(err, 'Kunde inte spara')));
    };

    const deletePrice = (id: number) => {
        deletePriceRow(id)
            .then(() => { loadPrices(); loadSpec(); })
            .catch(err => setPriceMsg(getErrorMessage(err, 'Kunde inte ta bort')));
    };

    /** En rad per kategori med ett fält per trappsteg. Tomt fält = steget saknas, närmast lägre pris gäller. */
    const renderPriceGrid = (tenant: string) => (
        <table style={{ borderCollapse: 'collapse' }}>
            <thead>
                <tr>
                    <th style={{ ...th, paddingLeft: 0 }}>Typ</th>
                    {tiers.map(t => <th key={t.min} style={thRight}>{t.label}</th>)}
                </tr>
            </thead>
            <tbody>
                {CATEGORY_ORDER.map(category => (
                    <tr key={category}>
                        <td style={{ ...td, paddingLeft: 0, whiteSpace: 'nowrap' }}>{labels[category]}</td>
                        {tiers.map(t => {
                            const applicable = t.min === 1 || isTiered(category);
                            if (!applicable) return <td key={t.min} style={{ ...tdRight, color: '#bbb' }}>–</td>;
                            const unit = isQuantity(category) ? ' st' : category === 'sms' ? ' kr/sms' : '';
                            const row = rowFor(tenant, category, t.min);
                            const key = draftKey(tenant, category, t.min);
                            const value = priceDrafts[key] ?? (row ? String(row.monthly_price) : '');
                            const dirty = priceDrafts[key] !== undefined && priceDrafts[key] !== (row ? String(row.monthly_price) : '');
                            const canDelete = row && !(tenant === '' && t.min === 1);
                            return (
                                <td key={t.min} style={{ ...tdRight }}>
                                    <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                                        <input type="text" inputMode="decimal" value={value} placeholder={row ? '' : 'som lägre'}
                                            onChange={e => setPriceDrafts(d => ({ ...d, [key]: e.target.value }))}
                                            onKeyDown={e => { if (e.key === 'Enter') savePrice(tenant, category, t.min); }}
                                            style={priceInput} />
                                        {unit && <span style={{ fontSize: 12, color: '#888' }}>{unit}</span>}
                                        {dirty && <button onClick={() => savePrice(tenant, category, t.min)} style={ui.btnLight}>Spara</button>}
                                        {!dirty && canDelete && <button onClick={() => deletePrice(row!.id)} title="Ta bort steget" style={ui.btnLightDanger}>✕</button>}
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const tenantsWithOwnPrices = Array.from(new Set((priceList?.prices ?? []).filter(p => p.tenant_id !== '').map(p => p.tenant_id)));
    const tenantName = (id: string) => tenants.find(t => t.id === id)?.display_name || id;

    const monthIsCurrent = month === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    const monthIsFuture = month > `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

    /** Ett pris på typraden bara när det är ett enda; med trappa står priset på varje enhetsrad i stället. */
    const tierText = (c: SpecCategory) => {
        if (!c.tiers.length) return kr(0);
        if (c.category === 'sms') return `${kr(c.tiers[0].price)}/sms`;
        if (!c.tiered || c.tiers.length === 1) return kr(c.tiers[0].price);
        return '';
    };

    /** Sms-rader räknas i styck, övriga i dagar. */
    const perPiece = (c: SpecCategory) => c.category === 'sms';

    return (
        <div>
            <PageHeader className="no-print" icon="💰" title="Fakturaunderlag"
                description="Välj kund och månad så räknas underlaget fram direkt ur databasen. Inget sparas här, fakturan och dess historik ligger i fakturaprogrammet." />

            {/* ── Val av kund och månad ─────────────────────────── */}
            <div style={{ ...card, display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }} className="no-print">
                <div>
                    <label style={ui.label}>Kund</label>
                    <select value={tenantId} onChange={e => setTenantId(e.target.value)}
                        style={{ ...ui.input, width: 'auto', minWidth: 220 }}>
                        {tenants.map(t => <option key={t.id} value={t.id}>{t.display_name}</option>)}
                    </select>
                </div>
                <div>
                    <label style={ui.label}>Månad</label>
                    <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                        style={{ ...ui.input, width: 'auto' }} />
                </div>
                <label style={{ fontSize: '13px', color: '#444', display: 'flex', alignItems: 'center', gap: '6px', paddingBottom: '8px' }}>
                    <input type="checkbox" checked={showDetails} onChange={e => setShowDetails(e.target.checked)} />
                    Visa varje enhet
                </label>
                <button onClick={() => window.print()} style={{ ...ui.btnLightNeutral, marginLeft: 'auto' }}>
                    🖨️ Skriv ut
                </button>
            </div>

            {/* ── Moduler för vald kund ─────────────────────────── */}
            {tenantId && <TenantModules tenantId={tenantId} tenantName={tenantName(tenantId)} />}

            {error && <div style={ui.errorBox}>{error}</div>}
            {specLoading && <div style={{ color: '#888', padding: '12px 0' }}>Räknar fram underlaget…</div>}

            {/* ── Specifikation ─────────────────────────────────── */}
            {spec && !specLoading && (
                <div style={card} className="billing-spec">
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '4px' }}>
                        <h3 style={{ margin: 0, color: '#1a1a2e', fontSize: '17px' }}>
                            {spec.tenant.display_name} — {spec.month}
                        </h3>
                        <span style={{ fontSize: '13px', color: '#888' }}>{spec.daysInMonth} dagar i månaden</span>
                    </div>
                    {(monthIsCurrent || monthIsFuture) && (
                        <div style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>
                            {monthIsFuture
                                ? 'Månaden har inte börjat än — siffrorna är en förhandsvisning.'
                                : 'Månaden pågår — enheter som läggs till senare i månaden kommer med när de skapas.'}
                        </div>
                    )}

                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                        <thead>
                            <tr>
                                <th style={th}>Typ</th>
                                <th style={thRight}>Antal</th>
                                <th style={thRight}>Pris/mån</th>
                                <th style={thRight}>Dagar</th>
                                <th style={thRight}>Summa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {spec.categories.map(c => (
                                <React.Fragment key={c.category}>
                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                        <td style={{ ...td, fontWeight: 700 }}>{perPiece(c) ? 'Sms' : c.label}</td>
                                        <td style={tdRight}>{c.count}{perPiece(c) ? ' st' : ''}</td>
                                        <td style={tdRight}>{tierText(c)}</td>
                                        <td style={tdRight}>{perPiece(c) ? '–' : c.totalDays}</td>
                                        <td style={{ ...tdRight, fontWeight: 700 }}>{kr(c.amount)}</td>
                                    </tr>
                                    {showDetails && c.units.map((u, i) => (
                                        <tr key={`${c.category}-${i}`}>
                                            <td style={{ ...td, paddingLeft: '32px', color: '#555' }}>
                                                {u.name}
                                                {u.included && u.type !== 'sms' && (
                                                    <span style={{ color: '#2a7', fontSize: '12px' }}> (ingår i grundavgiften)</span>
                                                )}
                                                {u.deleted && (
                                                    <span style={{ color: '#c77', fontSize: '12px' }}> (borttagen)</span>
                                                )}
                                                {!u.deleted && u.disabledPart && (
                                                    <span style={{ color: '#999', fontSize: '12px' }}> (avstängd del av månaden)</span>
                                                )}
                                                {!u.deleted && !u.disabledPart && !u.included && u.quantity === undefined && u.days < spec.daysInMonth && (
                                                    <span style={{ color: '#999', fontSize: '12px' }}> (ny under månaden)</span>
                                                )}
                                            </td>
                                            <td style={{ ...tdRight, color: '#555' }}>{u.quantity !== undefined ? `${u.quantity} st` : ''}</td>
                                            <td style={{ ...tdRight, color: '#555' }}>{u.included ? '' : u.type === 'sms' ? `${kr(u.unitPrice)}/sms` : kr(u.unitPrice)}</td>
                                            <td style={{ ...tdRight, color: '#555' }}>{u.quantity !== undefined ? '–' : u.days}</td>
                                            <td style={{ ...tdRight, color: '#555' }}>{kr(u.amount)}</td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td style={{ ...td, fontWeight: 700, fontSize: '15px', borderTop: '2px solid #1a1a2e', borderBottom: 'none' }}>
                                    Att fakturera
                                </td>
                                <td style={{ ...td, borderTop: '2px solid #1a1a2e', borderBottom: 'none' }} colSpan={3}></td>
                                <td style={{ ...tdRight, fontWeight: 700, fontSize: '15px', borderTop: '2px solid #1a1a2e', borderBottom: 'none' }}>
                                    {kr(spec.total)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                    <p style={{ color: '#999', fontSize: '12px', marginTop: '12px', marginBottom: 0 }}>
                        En enhet räknas från den dag den lades in till den dag den togs bort,
                        annars till månadens slut. En avstängd enhet räknas till den dag den
                        senast skickade data; har den varit avstängd hela månaden visas den inte.
                        Ett pausat schema räknas till den dag det pausades, på samma sätt.
                        Priset per enhet följer trappan: de äldsta enheterna räknas först.
                        Sms räknas per skickat sms: ett antal ingår per samordnare som var med under
                        månaden, resten kostar per styck utan trappa.
                        Alla priser utan moms.
                    </p>
                </div>
            )}

            {/* ── Sms per kund ──────────────────────────────────── */}
            {smsOverview && smsOverview.tenants.length > 0 && (
                <div style={card} className="no-print">
                    <h3 style={{ margin: '0 0 4px', color: '#1a1a2e', fontSize: '15px' }}>Sms per kund — {smsOverview.month}</h3>
                    <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px' }}>
                        Antal sms som gick iväg under månaden, alla slags utskick inräknade. Vad som ingår och kostar per kund syns i underlaget ovan.
                    </p>
                    <table style={{ borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ ...th, paddingLeft: 0 }}>Kund</th>
                                <th style={thRight}>Sms</th>
                            </tr>
                        </thead>
                        <tbody>
                            {smsOverview.tenants.map(t => (
                                <tr key={t.tenant} style={{ fontWeight: t.tenant === tenantId ? 700 : 400 }}>
                                    <td style={{ ...td, paddingLeft: 0, cursor: 'pointer' }} onClick={() => setTenantId(t.tenant)}>{t.display_name}</td>
                                    <td style={tdRight}>{t.sms_sent === null ? <span style={{ color: '#c77' }}>gick inte att hämta</span> : t.sms_sent}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Prislista ─────────────────────────────────────── */}
            <div style={card} className="no-print">
                <button onClick={() => setShowPrices(v => !v)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: 700, color: '#1a1a2e', padding: 0 }}>
                    {showPrices ? '▾' : '▸'} Prislista
                </button>

                {showPrices && priceList && (
                    <div style={{ marginTop: '16px' }}>
                        <h4 style={{ margin: '0 0 4px', fontSize: '13px', color: '#666', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                            Grundpriser (kr per månad och enhet)
                        </h4>
                        <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px' }}>
                            Trappan räknas per enhet: de fem första kostar första steget, nummer sex till tio nästa, och så vidare.
                            Lämna ett steg tomt så gäller närmast lägre pris. Grundavgift och modul har bara ett pris.
                            Sms: ange hur många som ingår per samordnare och månad, och priset per sms utöver det. Ingen trappa.
                        </p>
                        {renderPriceGrid('')}

                        <h4 style={{ margin: '24px 0 4px', fontSize: '13px', color: '#666', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                            Egna priser per kund
                        </h4>
                        <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px' }}>
                            Har kunden ett eget pris i en typ används kundens trappa för hela den typen, annars grundpriserna.
                        </p>
                        {tenantsWithOwnPrices.map(t => (
                            <div key={t} style={{ marginBottom: '16px' }}>
                                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>{tenantName(t)}</div>
                                {renderPriceGrid(t)}
                            </div>
                        ))}
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px' }}>
                            <select value={overrideTenant} onChange={e => setOverrideTenant(e.target.value)}
                                style={{ ...ui.input, width: 'auto', fontSize: 13 }}>
                                <option value="">Lägg till eget pris för kund…</option>
                                {tenants.filter(t => !tenantsWithOwnPrices.includes(t.id)).map(t => <option key={t.id} value={t.id}>{t.display_name}</option>)}
                            </select>
                        </div>
                        {overrideTenant && !tenantsWithOwnPrices.includes(overrideTenant) && (
                            <div style={{ marginTop: '12px' }}>
                                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>{tenantName(overrideTenant)}</div>
                                {renderPriceGrid(overrideTenant)}
                            </div>
                        )}
                        {priceMsg && <div style={{ marginTop: '10px', fontSize: '13px', color: priceMsg === 'Sparat' ? colors.success : colors.danger }}>{priceMsg}</div>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Billing;
